import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Truck, RotateCcw, ShieldCheck, Minus, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import ProductImage from "@/components/ProductImage";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { isPurchasable, OUT_OF_STOCK_LABEL } from "@/lib/inventory";
import { useStore } from "@/context/StoreContext";
import { cacheGet, cacheKey, fetchCached } from "@/lib/dataCache";
import SizeGuideModal from "@/components/SizeGuideModal";
import { getStylingTips } from "@/lib/stylingTips";
import { defaultProductSize } from "@/lib/sizes";
import { swatchForColor } from "@/lib/colorSwatches";
import ProductCard from "@/components/ProductCard";
import RecentlyViewed from "@/components/RecentlyViewed";
import ColorVariants from "@/components/ColorVariants";
import ProductImageLightbox from "@/components/ProductImageLightbox";
import { prefetchProductImages } from "@/lib/images";
import { policyPath } from "@/lib/policyMeta";
import { SHIPPING_FEE } from "@/lib/shipping";
import { useHorizontalSwipe } from "@/lib/useHorizontalSwipe";
import SeoHead from "@/components/SeoHead";
import { primaryProductId, productSeo } from "@/lib/seoConfig";
import { breadcrumbSchema, globalSchemaGraph, productSchema } from "@/lib/seoSchema";
import { productImageSrc } from "@/lib/images";

function ProductMainGallery({
  productName,
  displayImage,
  productImages,
  activeImageIndex,
  hasMultipleImages,
  onOpenLightbox,
  onPrev,
  onNext,
}) {
  const { swipeHandlers, consumeSwipeClick } = useHorizontalSwipe({
    enabled: hasMultipleImages,
    onSwipeLeft: onNext,
    onSwipeRight: onPrev,
  });

  return (
    <div
      className="aspect-[3/4] bg-muted overflow-hidden relative isolate touch-pan-y"
      data-testid="product-gallery"
      {...swipeHandlers}
    >
      <button
        type="button"
        onClick={() => {
          if (consumeSwipeClick()) return;
          onOpenLightbox();
        }}
        className="block h-full w-full cursor-zoom-in text-left"
        aria-label="Open image gallery"
        data-testid="product-main-image"
      >
        <ProductImage
          src={displayImage}
          alt={productName}
          priority
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="h-full w-full object-cover object-[center_30%]"
        />
      </button>
      {hasMultipleImages && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-2 top-1/2 z-10 -translate-y-1/2 w-9 h-9 rounded-full bg-background/80 backdrop-blur-sm border border-border inline-flex items-center justify-center hover:bg-background transition"
            aria-label="Previous photo"
            data-testid="gallery-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 w-9 h-9 rounded-full bg-background/80 backdrop-blur-sm border border-border inline-flex items-center justify-center hover:bg-background transition"
            aria-label="Next photo"
            data-testid="gallery-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2 right-2 z-10 text-[10px] uppercase tracking-[0.16em] bg-background/80 backdrop-blur-sm px-2 py-1 border border-border">
            {activeImageIndex + 1}/{productImages.length}
          </div>
          <div
            className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1.5 sm:hidden"
            aria-hidden
            data-testid="gallery-dots"
          >
            {productImages.map((img, idx) => (
              <span
                key={`${img}-${idx}`}
                className={`h-1.5 rounded-full transition-all ${
                  idx === activeImageIndex ? "w-4 bg-primary" : "w-1.5 bg-primary/35"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const Product = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [colorVariants, setColorVariants] = useState([]);
  const [activeImage, setActiveImage] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { addToCart, toggleWishlist, inWishlist, trackRecentlyViewed } = useStore();

  useEffect(() => {
    const key = cacheKey(["product", id]);
    const cached = cacheGet(key);
    setNotFound(false);

    if (cached?.product) {
      setProduct(cached.product);
      setRelated(cached.related || []);
      setColorVariants(cached.color_variants || []);
      setActiveImage((cached.product.images?.[0] || cached.product.image || ""));
      setSize(defaultProductSize(cached.product.sizes));
      setQty(1);
    } else {
      setProduct(null);
      setRelated([]);
      setColorVariants([]);
    }

    let active = true;
    fetchCached(key, () => api.get(`/products/${id}`).then((r) => r.data))
      .then((data) => {
        if (!active) return;
        setProduct(data.product);
        setRelated(data.related || []);
        setColorVariants(data.color_variants || []);
        setActiveImage((data.product.images?.[0] || data.product.image || ""));
        setSize(defaultProductSize(data.product.sizes));
        setQty(1);
        setNotFound(false);
        window.scrollTo({ top: 0 });
      })
      .catch(() => {
        if (active && !cached?.product) {
          setProduct(null);
          setNotFound(true);
        }
      });

    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (product?.id === id) trackRecentlyViewed(product);
  }, [product, id, trackRecentlyViewed]);

  useEffect(() => {
    if (!product) return;
    const imgs = product.images?.length ? product.images : product.image ? [product.image] : [];
    prefetchProductImages(imgs.map((image) => ({ image })), { limit: 8, variant: "md" });
  }, [product]);

  useEffect(() => {
    if (related.length) prefetchProductImages(related, { limit: 8 });
  }, [related]);

  const imageSwatches = useMemo(() => {
    if (product?.images?.length) return product.images;
    if (product?.image) return [product.image];
    return [];
  }, [product]);

  if (!product && notFound) {
    return (
      <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 py-24 text-center" data-testid="product-not-found">
        <h1 className="editorial-h1">Piece not found</h1>
        <p className="text-secondary mt-4">This product may have been removed or the link is incorrect.</p>
        <Link to="/" className="btn-primary mt-8 inline-flex">Back to home</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 py-20 grid lg:grid-cols-2 gap-12">
        <div className="aspect-[3/4] bg-muted animate-pulse" />
        <div className="space-y-4">
          <div className="h-4 w-24 bg-muted animate-pulse" />
          <div className="h-10 w-3/4 bg-muted animate-pulse" />
          <div className="h-4 w-1/2 bg-muted animate-pulse" />
        </div>
      </div>
    );
  }

  const fav = inWishlist(product.id);
  const available = isPurchasable(product);
  const maxQty = Number(product.stock_count ?? 1);
  const styling = getStylingTips(product);
  const productImages = product.images?.length ? product.images : (product.image ? [product.image] : []);
  const displayImage = activeImage || productImages[0] || product.image;
  const activeImageIndex = Math.max(0, productImages.findIndex((img) => img === displayImage));
  const hasMultipleImages = productImages.length > 1;

  const showPrevImage = () => {
    if (!hasMultipleImages) return;
    const nextIndex = activeImageIndex <= 0 ? productImages.length - 1 : activeImageIndex - 1;
    setActiveImage(productImages[nextIndex]);
  };

  const showNextImage = () => {
    if (!hasMultipleImages) return;
    const nextIndex = activeImageIndex >= productImages.length - 1 ? 0 : activeImageIndex + 1;
    setActiveImage(productImages[nextIndex]);
  };

  const openLightbox = (index = activeImageIndex) => {
    const img = productImages[index] || productImages[0];
    if (img) setActiveImage(img);
    setLightboxOpen(true);
  };

  const setLightboxIndex = (index) => {
    const img = productImages[index];
    if (img) setActiveImage(img);
  };

  const seo = productSeo(product, colorVariants);
  const schemaImage = productImageSrc(displayImage, "md");
  const canonicalId = primaryProductId(product, colorVariants) || product.id;
  const productJsonLd = [
    ...globalSchemaGraph(),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: product.category_name || "Shop", path: `/category/${product.category}` },
      { name: product.name, path: `/product/${canonicalId}` },
    ]),
    productSchema({ ...product, id: canonicalId }, schemaImage),
  ];

  return (
    <div data-testid="product-page">
      {seo ? (
        <SeoHead
          {...seo}
          ogType="product"
          ogImage={schemaImage || undefined}
          jsonLd={productJsonLd}
        />
      ) : null}
      <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 pt-8 lg:pt-12">
        <nav className="text-xs uppercase tracking-[0.22em] text-secondary flex flex-wrap gap-x-2 gap-y-1 items-center" data-testid="breadcrumb">
          <Link to="/" className="hover:opacity-70">Home</Link>
          <span>/</span>
          <Link to={`/category/${product.category}`} className="hover:opacity-70">{product.category_name}</Link>
          <span>/</span>
          <span className="text-primary truncate">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 mt-6 lg:mt-10 grid lg:grid-cols-12 gap-8 lg:gap-16">
        {/* Gallery */}
        <div className="lg:col-span-7 min-w-0">
          <ProductMainGallery
            productName={product.name}
            displayImage={displayImage}
            productImages={productImages}
            activeImageIndex={activeImageIndex}
            hasMultipleImages={hasMultipleImages}
            onOpenLightbox={() => openLightbox(activeImageIndex)}
            onPrev={showPrevImage}
            onNext={showNextImage}
          />
          {hasMultipleImages && (
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="product-thumbnails">
              {productImages.map((img, idx) => {
                const selected = img === displayImage;
                return (
                  <button
                    key={`${img}-${idx}`}
                    type="button"
                    onClick={() => openLightbox(idx)}
                    className={`aspect-[3/4] bg-muted overflow-hidden border transition ${
                      selected ? "border-primary" : "border-border hover:border-primary/60"
                    }`}
                    data-testid={`product-thumb-${idx}`}
                    aria-label={`View image ${idx + 1}`}
                  >
                    <ProductImage src={img} alt={`${product.name} ${idx + 1}`} sizes="120px" className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="lg:col-span-5 min-w-0 lg:sticky lg:top-24 lg:self-start">
          <p className="overline" data-testid="product-category">{product.category_name}</p>
          <h1 className="serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight" data-testid="product-name">
            {product.name}
          </h1>
          <div className="mt-4 flex items-baseline gap-3 flex-wrap">
            <span className="text-xl font-medium" data-testid="product-price">{inr(product.price)}</span>
            {product.compare_at_price && (
              <span className="text-sm text-secondary line-through">{inr(product.compare_at_price)}</span>
            )}
            <span className="text-xs text-secondary">incl. of all taxes</span>
            {!available && (
              <span className="text-xs uppercase tracking-[0.22em] text-destructive">{OUT_OF_STOCK_LABEL}</span>
            )}
            {available && maxQty <= 2 && (
              <span className="text-xs uppercase tracking-[0.22em] text-amber-700">Only {maxQty} left</span>
            )}
          </div>
          <p className="mt-6 text-sm text-primary/80 leading-relaxed">{product.description}</p>

          <div className="mt-8 border-t border-border pt-6 space-y-3 text-sm text-primary/80" data-testid="styling-tips">
            <p className="overline">How to style it</p>
            <p><span className="text-secondary">Wear it to:</span> {styling.wear}</p>
            <p><span className="text-secondary">Pair it with:</span> {styling.pair}</p>
            <p><span className="text-secondary">Not ideal for:</span> {styling.avoid}</p>
          </div>

          {/* Color / photos */}
          <div className="mt-8">
            <ColorVariants
              variants={colorVariants}
              currentId={product.id}
              currentColor={product.color}
            />
            {colorVariants.length <= 1 && hasMultipleImages && (
              <div className="mt-4">
                <p className="overline mb-2">Photos</p>
                <div className="flex flex-wrap gap-2" data-testid="image-color-swatches">
                  {imageSwatches.map((img, idx) => {
                    const selected = img === displayImage;
                    return (
                      <button
                        key={`img-swatch-${img}-${idx}`}
                        type="button"
                        onClick={() => img && setActiveImage(img)}
                        className={`relative w-10 h-12 overflow-hidden border transition ${
                          selected ? "border-primary ring-1 ring-primary/30" : "border-border hover:border-primary"
                        }`}
                        aria-label={`View photo ${idx + 1}`}
                        aria-pressed={selected}
                        data-testid={`image-color-swatch-${idx}`}
                      >
                        <ProductImage
                          src={img}
                          alt={`${product.name} ${idx + 1}`}
                          sizes="40px"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {colorVariants.length <= 1 && !hasMultipleImages && product.color && (
              <div>
                <p className="overline mb-2">Colour</p>
                <div className="flex items-center gap-2">
                  <span
                    className="w-6 h-6 border border-border shrink-0"
                    style={{ backgroundColor: swatchForColor(product.color) }}
                    aria-hidden
                  />
                  <span className="text-sm">{product.color}</span>
                </div>
              </div>
            )}
          </div>

          {/* Size */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Size</p>
              <button
                type="button"
                onClick={() => setSizeGuideOpen(true)}
                className="text-xs underline underline-offset-4"
                data-testid="size-guide-toggle"
              >
                Size guide
              </button>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="size-selector">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  data-testid={`size-${s}`}
                  className={`min-h-11 text-sm border transition ${
                    size === s
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border hover:border-primary"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Qty */}
          <div className="mt-6 flex items-center gap-6">
            <p className="overline">Quantity</p>
            <div className="inline-flex items-center border border-border" data-testid="qty-selector">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="min-h-11 min-w-11 inline-flex items-center justify-center" aria-label="Decrease quantity"><Minus className="w-4 h-4" /></button>
              <span className="px-4 text-sm min-w-[2rem] text-center">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="min-h-11 min-w-11 inline-flex items-center justify-center" disabled={!available} aria-label="Increase quantity"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
            <button
              onClick={() => addToCart(product, size, qty, displayImage)}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-to-cart-btn"
              disabled={!available}
            >
              {available ? (
                <>
                  <span className="sm:hidden">Add — {inr(product.price * qty)}</span>
                  <span className="hidden sm:inline">Add to bag — {inr(product.price * qty)}</span>
                </>
              ) : OUT_OF_STOCK_LABEL}
            </button>
            <button
              onClick={() => toggleWishlist(product)}
              data-testid="pdp-wishlist-btn"
              className="btn-outline sm:px-5"
              aria-label="Wishlist"
            >
              <Heart className={`w-4 h-4 ${fav ? "fill-primary" : ""}`} />
            </button>
          </div>

          {/* Promise */}
          <div className="mt-10 border-t border-border pt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-secondary">
            <div className="flex flex-col items-center text-center gap-1.5">
              <Truck className="w-4 h-4" />
              <span>Flat shipping ₹{SHIPPING_FEE}</span>
            </div>
            <div className="flex flex-col items-center text-center gap-1.5">
              <RotateCcw className="w-4 h-4" />
              <span>24-hr issue reporting</span>
            </div>
            <div className="flex flex-col items-center text-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              <span>Authenticity assured</span>
            </div>
          </div>

          {/* Accordion */}
          <div className="mt-10 divide-y divide-border border-t border-b border-border">
            {[
              ["Composition & Care", "Premium fabric, lined. Dry clean only. Store on hanger."],
              ["Shipping", `Dispatched within 1–2 business days. Flat shipping ₹${SHIPPING_FEE} on every order. Metro delivery typically 5–7 business days.`],
              ["Returns", "Report wrong, damaged, or missing items within 24 hours of delivery with a mandatory unboxing video. Exchange or store credit on approved claims."],
            ].map(([t, b]) => (
              <details key={t} className="group py-4">
                <summary className="flex items-center justify-between cursor-pointer">
                  <span className="overline">{t}</span>
                  <Plus className="w-4 h-4 group-open:rotate-45 transition-transform" />
                </summary>
                <p className="mt-3 text-sm text-primary/75">{b}</p>
              </details>
            ))}
          </div>
          <p className="mt-4 text-xs text-secondary">
            <Link to={policyPath("shipping")} className="link-underline">Shipping Policy</Link>
            {" · "}
            <Link to={policyPath("returns")} className="link-underline">Exchange &amp; Returns</Link>
          </p>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 mt-24">
          <p className="overline mb-4">You may also like</p>
          <h2 className="editorial-h2 mb-10">Complete the <span className="italic">edit</span></h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      <RecentlyViewed
        excludeId={product.id}
        className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 mt-24 pb-16"
      />

      <SizeGuideModal
        open={sizeGuideOpen}
        onOpenChange={setSizeGuideOpen}
        product={product}
        selectedSize={size}
        onSelectSize={setSize}
      />

      <ProductImageLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        images={productImages}
        activeIndex={activeImageIndex}
        onActiveIndexChange={setLightboxIndex}
        productName={product.name}
      />
    </div>
  );
};

export default Product;
